const chapter = {
  id: 'determinants',
  slug: 'determinants',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Determinants',
        hindi: 'सारणिक',
  },
  estReadMinutes: 45,
  difficulty: {
    english: 'Easy–Medium',
        hindi: 'आसान-मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main',
        hindi: 'जेईई मेन में 1-2 प्रश्न',
  },
  syllabusTags: ['Algebra', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'def',
      title: {
        english: 'Determinant: Definition & Expansion',
        hindi: 'सारणिक: परिभाषा और प्रसार',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Determinant',
        hindi: 'निर्धारक',
          },
          body: {
            english: `A scalar associated with every square matrix. For order $1$: $|a| = a$. For order $2$:
$$\\begin{vmatrix} a & b \\\\ c & d \\end{vmatrix} = ad - bc.$$
For order $3$, expand along any row/column using cofactors:
$$|A| = \\sum_{j=1}^{3} a_{ij} C_{ij},\\quad C_{ij} = (-1)^{i+j} M_{ij}.$$`,
            hindi: 'प्रत्येक वर्ग आव्यूह से जुड़ा एक अदिश। ऑर्डर के लिए $1$: $|a| = a$। ऑर्डर $2$ के लिए: $$\\begin{vmatrix} a & b \\\\\\\\\\\\\\\\ c & d \\end{vmatrix} = ad - bc.$$ ऑर्डर $3$ के लिए, सहकारकों का उपयोग करके किसी भी row/column पर विस्तार करें: $$|A| = \\sum_{j=1}^{3} a_{ij} C_{ij},\\quad C_{ij} = (-1)^{i+j} M_{ij}.$$',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Evaluate $\\begin{vmatrix} 1 & 2 & 3 \\\\ 2 & 3 & 1 \\\\ 3 & 1 & 2 \\end{vmatrix}$.',
        hindi: '$\\begin{vmatrix} 1 & 2 & 3 \\\\ 2 & 3 & 1 \\\\ 3 & 1 & 2 \\end{vmatrix}$ का मूल्यांकन करें।',
          },
          solution: {
            english: 'Expand along $R_1$: $1(6 - 1) - 2(4 - 3) + 3(2 - 9) = 5 - 2 - 21 = -18$.',
        hindi: '$R_1$ के साथ विस्तार करें: $1(6 - 1) - 2(4 - 3) + 3(2 - 9) = 5 - 2 - 21 = -18$।',
          },
        },
      ],
    },
    {
      id: 'properties',
      title: {
        english: 'Key Properties',
        hindi: 'मुख्य गुणधर्म',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**P1**: $|A^T| = |A|$.',
        hindi: '**P1**: $|A^T| = |A|$.',
            },
            {
              english: '**P2**: Swap two rows (or columns) ⇒ sign of $|A|$ flips.',
        hindi: '**पी2**: दो पंक्तियों (या स्तंभों) को स्वैप करें ⇒ $|A|$ फ़्लिप का चिह्न।',
            },
            {
              english: '**P3**: Two identical rows/columns ⇒ $|A| = 0$.',
        hindi: '**P3**: दो समान पंक्तियाँ/स्तंभ ⇒ $|A| = 0$.',
            },
            {
              english: '**P4**: Multiply a row by $k$ ⇒ $|A|$ multiplies by $k$. Hence $|kA| = k^n |A|$ for $n \\times n$.',
        hindi: '**P4**: एक पंक्ति को $k$ से गुणा करें ⇒ $|A|$ को $k$ से गुणा करें। इसलिए $n \\times n$ के लिए $|kA| = k^n |A|$।',
            },
            {
              english: '**P5**: $R_i \\to R_i + k R_j$ ($i \\ne j$) leaves $|A|$ unchanged.',
        hindi: '**P5**: $R_i \\to R_i + k R_j$ ($i \\ne j$) $|A|$ को अपरिवर्तित छोड़ देता है।',
            },
            {
              english: '**P6**: If a row is the sum of two row-vectors, the determinant splits as a sum.',
        hindi: '**पी6**: यदि एक पंक्ति दो पंक्ति-सदिशों का योग है, तो निर्धारक योग के रूप में विभाजित होता है।',
            },
            {
              english: '**P7**: $|AB| = |A| \\cdot |B|$.',
        hindi: '**पी7**: $|एबी| = |ए| \\cdot |B|$.',
            },
            {
              english: '**P8**: $|A^{-1}| = 1/|A|$ (when $A$ is invertible).',
        hindi: '**P8**: $|A^{-1}| = 1/|ए|$ (जब $ए$ उलटा है)।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Use **row/column reduction** (P5) to create zeros, then expand along that row/column for fast evaluation.',
        hindi: 'शून्य बनाने के लिए **row/column कमी** (P5) का उपयोग करें, फिर तेजी से मूल्यांकन के लिए उस row/column के साथ विस्तार करें।',
          },
        },
      ],
    },
    {
      id: 'minors',
      title: {
        english: 'Minors, Cofactors & Adjoint',
        hindi: 'उपसारणिक, सहखण्ड और सहायक',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Minor & Cofactor',
        hindi: 'लघु एवं सहकारक',
          },
          body: {
            english: 'Minor $M_{ij}$ = determinant after deleting row $i$, column $j$. Cofactor $C_{ij} = (-1)^{i+j} M_{ij}$.',
        hindi: 'माइनर $M_{ij}$ = पंक्ति $i$, कॉलम $j$ को हटाने के बाद निर्धारक। सहकारक $C_{ij} = (-1)^{i+j} M_{ij}$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Expansion**: $|A| = \\sum_j a_{ij} C_{ij}$ along any row $i$.',
        hindi: '**विस्तार**: $|A| = \\sum_j a_{ij} C_{ij}$ किसी भी पंक्ति के साथ $i$।',
            },
            {
              english: '**Wrong-row sum** (orthogonality): $\\sum_j a_{ij} C_{kj} = 0$ when $i \\ne k$.',
        hindi: '**गलत-पंक्ति योग** (ऑर्थोगोनैलिटी): $\\sum_j a_{ij} C_{kj} = 0$ जब $i \\ne k$।',
            },
            {
              english: '**adj$(A)$** = transpose of cofactor matrix.',
        hindi: '**adj$(A)$** = सहकारक आव्यूह का स्थानांतरण।',
            },
            {
              english: '$A \\cdot \\text{adj}(A) = |A| I$, hence $A^{-1} = \\dfrac{\\text{adj}(A)}{|A|}$ when $|A| \\ne 0$.',
        hindi: '$A \\cdot \\text{adj}(A) = |A| I$, इसलिए $A^{-1} = \\dfrac{\\text{adj}(A)}{|A|}$ जब $|A| \\ne 0$।',
            },
          ],
        },
      ],
    },
    {
      id: 'cramer',
      title: {
        english: 'Cramer\'s Rule',
        hindi: 'क्रैमर का नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Cramer\'s rule',
        hindi: 'क्रैमर का नियम',
          },
          statement: {
            english: `For $AX = B$ with $A$ square and $\\Delta = |A| \\ne 0$:
$$x_i = \\dfrac{\\Delta_i}{\\Delta}$$
where $\\Delta_i$ is the determinant obtained by replacing column $i$ of $A$ by $B$.`,
            hindi: '$AX = B$ के लिए $A$ वर्ग और $\\Delta = |A| \\ne 0$ के साथ: $$x_i = \\dfrac{\\Delta_i}{\\Delta}$$ जहां $\\Delta_i$ $A$ के कॉलम $i$ को $B$ से बदलने पर प्राप्त निर्धारक है। शीर्षों वाला त्रिभुज $AX = B$: $$x_i = \\dfrac{\\Delta_i}{\\Delta}$$ **संरेखता**: यदि यह निर्धारक $A$ के बराबर है तो तीन बिंदु संरेख हैं।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'If $\\Delta \\ne 0$: unique solution.',
        hindi: 'यदि $\\Delta \\ne 0$: अद्वितीय समाधान।',
            },
            {
              english: 'If $\\Delta = 0$ and any $\\Delta_i \\ne 0$: no solution (inconsistent).',
        hindi: 'यदि $\\Delta = 0$ और कोई $\\Delta_i \\ne 0$: कोई समाधान नहीं (असंगत)।',
            },
            {
              english: 'If $\\Delta = 0$ and all $\\Delta_i = 0$: infinitely many or no solution (analyze further).',
        hindi: 'यदि $\\Delta = 0$ और सभी $\\Delta_i = 0$: अपरिमित रूप से अनेक या कोई समाधान नहीं (आगे विश्लेषण करें)।',
            },
            {
              english: 'For homogeneous system $AX = 0$: non-trivial solution iff $\\Delta = 0$.',
        hindi: 'सजातीय प्रणाली $AX = 0$ के लिए: गैर-तुच्छ समाधान यदि $\\Delta = 0$।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Solve $x + 2y = 4,\\ 3x - y = 5$ using Cramer\'s rule.',
        hindi: 'क्रैमर नियम का उपयोग करके $x + 2y = 4,\\\\ 3x - y = 5$ को हल करें।',
          },
          solution: {
            english: '$\\Delta = -1 - 6 = -7$. $\\Delta_1 = \\begin{vmatrix} 4 & 2 \\\\ 5 & -1 \\end{vmatrix} = -14$. $\\Delta_2 = \\begin{vmatrix} 1 & 4 \\\\ 3 & 5 \\end{vmatrix} = -7$. So $x = 2, y = 1$.',
        hindi: '$\\Delta = -1 - 6 = -7$. $\\Delta_1 = \\begin{vmatrix} 4 & 2 \\\\ 5 & -1 \\end{vmatrix} = -14$. $\\Delta_2 = \\begin{vmatrix} 1 & 4 \\\\ 3 & 5 \\end{vmatrix} = -7$. तो $x = 2, y = 1$.',
          },
        },
      ],
    },
    {
      id: 'standard',
      title: {
        english: 'Standard Determinants',
        hindi: 'मानक सारणिक',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Vandermonde',
        hindi: 'वेंडरमोंडे',
          },
          statement: {
            english: '$$\\begin{vmatrix} 1 & 1 & 1 \\\\ a & b & c \\\\ a^2 & b^2 & c^2 \\end{vmatrix} = (b - a)(c - a)(c - b).$$',
        hindi: '$$\\begin{vmatrix} 1 & 1 & 1 \\\\ a & b & c \\\\ a^2 & b^2 & c^2 \\end{vmatrix} = (b - a)(c - a)(c - b).$$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$\\begin{vmatrix} 1 & a & a^2 \\\\ 1 & b & b^2 \\\\ 1 & c & c^2 \\end{vmatrix} = (a - b)(b - c)(c - a)$ (signs may vary by row order).',
        hindi: '$\\begin{vmatrix} 1 & a & a^2 \\\\ 1 & b & b^2 \\\\ 1 & c & c^2 \\end{vmatrix} = (a - b)(b - c)(c - a)$ (चिह्न पंक्ति क्रम के अनुसार भिन्न हो सकते हैं)।',
            },
            {
              english: '$\\begin{vmatrix} a & b & c \\\\ b & c & a \\\\ c & a & b \\end{vmatrix} = -(a^3 + b^3 + c^3 - 3abc)$.',
        hindi: '$\\begin{vmatrix} a & b & c \\\\ b & c & a \\\\ c & a & b \\end{vmatrix} = -(a^3 + b^3 + c^3 - 3abc)$.',
            },
            {
              english: '$\\begin{vmatrix} 1+a & 1 & 1 \\\\ 1 & 1+b & 1 \\\\ 1 & 1 & 1+c \\end{vmatrix} = abc\\left(1 + \\tfrac1a + \\tfrac1b + \\tfrac1c\\right)$.',
        hindi: '$\\begin{vmatrix} 1+a & 1 & 1 \\\\ 1 & 1+b & 1 \\\\ 1 & 1 & 1+c \\end{vmatrix} = abc\\left(1 + \\tfrac1a + \\tfrac1b + \\tfrac1c\\right)$.',
            },
          ],
        },
      ],
    },
    {
      id: 'area',
      title: {
        english: 'Area of Triangle & Equation of Line',
        hindi: 'त्रिभुज का क्षेत्रफल और रेखा का समीकरण',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Area via determinant',
        hindi: 'निर्धारक के माध्यम से क्षेत्र',
          },
          statement: {
            english: `Triangle with vertices $(x_1, y_1), (x_2, y_2), (x_3, y_3)$:
$$\\text{Area} = \\dfrac{1}{2}\\left|\\,\\begin{vmatrix} x_1 & y_1 & 1 \\\\ x_2 & y_2 & 1 \\\\ x_3 & y_3 & 1 \\end{vmatrix}\\,\\right|.$$
**Collinearity**: three points are collinear iff this determinant equals $0$.`,
            hindi: 'शीर्षों वाला त्रिभुज $(x_1, y_1), (x_2, y_2), (x_3, y_3)$: $$\\text{Area} = \\dfrac{1}{2}\\left|\\,\\begin{vmatrix} x_1 & y_1 & 1 \\\\\\\\\\\\\\\\ x_2 & y_2 & 1 \\\\\\\\\\\\\\\\ x_3 & y_3 & 1 \\end{vmatrix}\\,\\right|.$$ **संरेखता**: यदि यह निर्धारक $0$ के बराबर है तो तीन बिंदु संरेख हैं।',
          },
        },
        {
          type: 'paragraph',
          text: {
            english: `Equation of the line through $(x_1, y_1)$ and $(x_2, y_2)$:
$$\\begin{vmatrix} x & y & 1 \\\\ x_1 & y_1 & 1 \\\\ x_2 & y_2 & 1 \\end{vmatrix} = 0.$$`,
            hindi: '$(x_1, y_1)$ और $(x_2, y_2)$ से होकर गुजरने वाली रेखा का समीकरण: $$\\begin{vmatrix} x & y & 1 \\\\\\\\\\\\\\\\ x_1 & y_1 & 1 \\\\\\\\\\\\\\\\ x_2 & y_2 & 1 \\end{vmatrix} = 0.$$',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find area of triangle with vertices $(1, 2), (3, 5), (-1, 4)$.',
        hindi: 'शीर्षों वाले त्रिभुज का क्षेत्रफल ज्ञात करें $(1, 2), (3, 5), (-1, 4)$।',
          },
          solution: {
            english: '$\\tfrac{1}{2}|1(5-4) - 2(3-(-1)) + 1(12-(-5))| = \\tfrac{1}{2}|1 - 8 + 17| = 5$ sq units.',
        hindi: '$\\tfrac{1}{2}|1(5-4) - 2(3-(-1)) + 1(12-(-5))| = \\tfrac{1}{2}|1 - 8 + 17| = 5$ वर्ग इकाई.',
          },
        },
      ],
    },
    {
      id: 'differentiation',
      title: {
        english: 'Differentiation of Determinants',
        hindi: 'सारणिकों का अवकलन',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Row-wise differentiation',
        hindi: 'पंक्ति-वार विभेदन',
          },
          statement: {
            english: `If $\\Delta(x) = \\begin{vmatrix} R_1(x) \\\\ R_2(x) \\\\ R_3(x) \\end{vmatrix}$, then
$$\\Delta'(x) = \\begin{vmatrix} R_1' \\\\ R_2 \\\\ R_3 \\end{vmatrix} + \\begin{vmatrix} R_1 \\\\ R_2' \\\\ R_3 \\end{vmatrix} + \\begin{vmatrix} R_1 \\\\ R_2 \\\\ R_3' \\end{vmatrix}.$$
(Same idea works column-wise.)`,
            hindi: 'यदि $\\Delta(x) = \\begin{vmatrix} R_1(x) \\\\\\\\\\\\\\\\ R_2(x) \\\\\\\\\\\\\\\\ R_3(x) \\end{vmatrix}$, तो $$\\Delta\\\'(x) = \\begin{vmatrix} R_1\\\' \\\\\\\\\\\\\\\\ R_2 \\\\\\\\\\\\\\\\ R_3 \\end{vmatrix} + \\begin{vmatrix} R_1 \\\\\\\\\\\\\\\\ R_2\\\' \\\\\\\\\\\\\\\\ R_3 \\end{vmatrix} + \\begin{vmatrix} R_1 \\\\\\\\\\\\\\\\ R_2 \\\\\\\\\\\\\\\\ R_3\\\' \\end{vmatrix}.$$ (एक ही विचार कॉलम-वार काम करता है।)',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '|A B| = |A|\\,|B|',
      desc: {
        english: 'Multiplicative',
        hindi: 'गुणक',
      },
    },
    {
      latex: '|kA| = k^n |A|',
      desc: {
        english: 'Scalar (n×n)',
        hindi: 'अदिश (n×n)',
      },
    },
    {
      latex: '|A^{-1}| = 1/|A|',
      desc: {
        english: '',
        hindi: '|ए -1 | = 1/|ए|',
      },
    },
    {
      latex: '|\\text{adj}\\,A| = |A|^{n-1}',
      desc: {
        english: '',
        hindi: '|adj\\,A| = |ए| एन-1',
      },
    },
    {
      latex: 'x_i = \\Delta_i / \\Delta',
      desc: {
        english: 'Cramer',
        hindi: 'क्रैमर',
      },
    },
    {
      latex: 'V = \\prod_{i<j}(x_j - x_i)',
      desc: {
        english: 'Vandermonde',
        hindi: 'वेंडरमोंडे',
      },
    },
    {
      latex: '\\text{Area} = \\tfrac12 |\\,\\det\\,|',
      desc: {
        english: 'Triangle area',
        hindi: 'त्रिभुज क्षेत्र',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'System',
      problem: {
        english: 'For what value of $\\lambda$ is the system $x + y + z = 6,\\ x + 2y + 3z = 10,\\ x + 2y + \\lambda z = \\mu$ consistent with infinitely many solutions?',
        hindi: '$\\lambda$ के किस मान के लिए सिस्टम $x + y + z = 6,\\\\ x + 2y + 3z = 10,\\\\ x + 2y + \\lambda z = \\mu$ अनंत रूप से कई समाधानों के अनुरूप है?',
      },
      answer: {
        english: '$\\lambda = 3, \\mu = 10$',
        hindi: '$\\lambda = 3, \\mu = 10$',
      },
      solution: {
        english: '$\\Delta = 0$ requires $\\lambda = 3$. For consistency, also $\\mu = 10$.',
        hindi: '$\\Delta = 0$ को $\\lambda = 3$ की आवश्यकता है। स्थिरता के लिए, $\\mu = 10$ भी।',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Property',
      problem: {
        english: 'If $|A| = 3$ for $3 \\times 3$ matrix $A$, find $|2 \\,\\text{adj}(3A)|$.',
        hindi: 'यदि $|ए| = 3$ $3 \\times 3$ मैट्रिक्स $A$ के लिए, $|2 \\,\\text{adj}(3A)|$ खोजें।',
      },
      answer: {
        english: '$2^3 \\cdot |adj(3A)| = 8 \\cdot |3A|^2 = 8 \\cdot (3^3 \\cdot 3)^2 = 8 \\cdot 81^2 = 52488$',
        hindi: '$2^3 \\cdot |adj(3A)| = 8 \\cdot |3A|^2 = 8 \\cdot (3^3 \\cdot 3)^2 = 8 \\cdot 81^2 = 52488$',
      },
      solution: {
        english: '$|3A| = 3^3 \\cdot 3 = 81$. $|adj(3A)| = 81^2$. Then $|2X| = 8|X|$.',
        hindi: '$|3ए| = 3^3 \\cdot 3 = 81$. $|adj(3ए)| = 81^2$. फिर $|2X| = 8|एक्स|$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Standard',
      problem: {
        english: 'Evaluate $\\begin{vmatrix} 1 & \\omega & \\omega^2 \\\\ \\omega & \\omega^2 & 1 \\\\ \\omega^2 & 1 & \\omega \\end{vmatrix}$ where $\\omega$ is a complex cube root of unity.',
        hindi: '$\\begin{vmatrix} 1 & \\omega & \\omega^2 \\\\ \\omega & \\omega^2 & 1 \\\\ \\omega^2 & 1 & \\omega \\end{vmatrix}$ का मूल्यांकन करें जहां $\\omega$ एकता का एक जटिल घनमूल है।',
      },
      answer: {
        english: '$0$',
        hindi: '$0$',
      },
      solution: {
        english: '$1 + \\omega + \\omega^2 = 0$ ⇒ $C_1 + C_2 + C_3 = 0$.',
        hindi: '$1 + \\omega + \\omega^2 = 0$ ⇒ $C_1 + C_2 + C_3 = 0$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Differentiation',
      problem: {
        english: 'If $f(x) = \\begin{vmatrix} x & x^2 & x^3 \\\\ 1 & 2x & 3x^2 \\\\ 0 & 2 & 6x \\end{vmatrix}$, find $f\'(x)$.',
        hindi: 'यदि $f(x) = \\begin{vmatrix} x & x^2 & x^3 \\\\ 1 & 2x & 3x^2 \\\\ 0 & 2 & 6x \\end{vmatrix}$, तो $f\'(x)$ खोजें।',
      },
      answer: {
        english: 'Use row-wise differentiation; algebraic simplification yields a polynomial in $x$.',
        hindi: 'पंक्ति-वार विभेदन का उपयोग करें; बीजीय सरलीकरण से $x$ में एक बहुपद प्राप्त होता है।',
      },
      solution: {
        english: 'Differentiate each row separately keeping others fixed and add the three resulting determinants.',
        hindi: 'अन्य को स्थिर रखते हुए प्रत्येक पंक्ति को अलग-अलग विभेदित करें और तीन परिणामी निर्धारकों को जोड़ें।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Cramer',
      problem: {
        english: 'Find values of $a$ for which the system $x + y + z = 1,\\ x + ay + a^2 z = a^3,\\ x + a^2 y + a^4 z = a^6$ has unique solution.',
        hindi: '$a$ के मान ज्ञात कीजिए जिसके लिए सिस्टम $x + y + z = 1,\\ x + ay + a^2 z = a^3,\\ x + a^2 y + a^4 z = a^6$ के पास अद्वितीय समाधान है।',
      },
      answer: {
        english: '$a \\notin \\{0, 1, -1\\}$',
        hindi: '$a \\notin \\{0, 1, -1\\}$',
      },
      solution: {
        english: 'Vandermonde-type determinant: $\\Delta = (a - 1)\\,a\\,(a + 1)\\cdots$; unique solution iff $\\Delta \\ne 0$.',
        hindi: 'वेंडरमोंडे-प्रकार निर्धारक: $\\Delta = (a - 1)\\,a\\,(a + 1)\\cdots$; अद्वितीय समाधान यदि $\\Delta \\ne 0$।',
      },
    },
  ],
  next: {
    slug: 'vectors',
    title: 'Vectors',
  },
};

export default chapter;
